import React from 'react'
import { styled } from '../stitches/stitches.config'
import { useActions, useActivityParams } from '@stackflow/react'
import CustomAppScreen from '../components/common/CustomAppScreen'
import { getTodayKey, isUpdatedAtToday } from '../utils/date'
import { Place as PlaceType } from '../types/Place'
import { DateKey } from '../types/DateKey'
import PageFeedWeekCalendar from '../components/pageFeed/PageFeedWeekCalendar'
import { getSevenDaysPlaces, removeAllPlaces } from '../api/search'
import { handleInitialPlaceDate } from '../utils/place'
import PageFeedContentTitle from '../components/pageFeed/PageFeedContentTitle'
import PageFeedPlaceList from '../components/pageFeed/PageFeedPlaceList'
import PageFeedCountDown from '../components/pageFeed/PageFeedCountDown'
import { PageCode } from '../stackflow/activities'
import { AiOutlineLogout } from 'react-icons/ai'
import { logout } from '../api/identify'

const FeedPage: React.FC = () => {
  const { replace } = useActions()
  const { groupCode } = useActivityParams<{ groupCode: string }>()
  const [allPlaces, setPlaces] = React.useState<PlaceType[]>([])
  const [selectedKey, setSelectedKey] = React.useState<DateKey>(getTodayKey())
  const isTodayNotUpdatedYet =
    selectedKey === getTodayKey() && !isUpdatedAtToday()

  const handleDateChange = (key: DateKey) => {
    setSelectedKey(key)
  }

  const handleLogout = () => {
    logout()
    removeAllPlaces()
    replace(PageCode.MAIN, {}, { animate: false })
  }

  React.useEffect(() => {
    const cb = async () => {
      const placesData = await getSevenDaysPlaces(groupCode)
      const places = placesData.map(handleInitialPlaceDate)
      setPlaces(places)
    }

    cb()
  }, [])

  React.useEffect(() => {
    if (!groupCode) {
      replace(PageCode.MAIN, {}, { animate: false })
    }
  }, [groupCode])

  const { places } = React.useMemo(() => {
    return {
      places: allPlaces.filter((place) => place.searchedAt === selectedKey),
    }
  }, [selectedKey, allPlaces])

  return (
    <CustomAppScreen
      appBar={{
        border: false,
        appendRight: () => {
          return (
            <LogoutIconWrapper onClick={handleLogout}>
              <AiOutlineLogout color="white" />
            </LogoutIconWrapper>
          )
        },
      }}
    >
      <Base>
        <PageFeedWeekCalendar
          groupCode={groupCode}
          selectedDateKey={selectedKey}
          onDateKeyCange={handleDateChange}
        />
        <PlaceContainer>
          <PageFeedContentTitle
            selectedDateKey={selectedKey}
            hasPlace={!!places.length}
          />
          {places.length ? (
            <PageFeedPlaceList places={places} groupCode={groupCode} />
          ) : isTodayNotUpdatedYet ? (
            <PageFeedCountDown />
          ) : null}
        </PlaceContainer>
      </Base>
    </CustomAppScreen>
  )
}

export default FeedPage

const Base = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  maxHeight: '100%',
  color: 'white',
})

const PlaceContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  background: '$gray00',
  height: '100%',
  borderRadius: '20px 20px 0 0',
  padding: '16px',
  overflowX: 'hidden',
  overflowY: 'scroll',
})

const LogoutIconWrapper = styled('div', {
  padding: '8px 8px 0 0',
})
